import { Injectable } from '@angular/core';
import { AgendaStatus } from '../../../models/agenda.model';

export interface StatusColorConfig {
  readonly bg: string;
  readonly border: string;
  readonly text: string;
}

@Injectable({
  providedIn: 'root'
})
export class AgendaStatusColorsService {

  private readonly statusColors: Readonly<Record<AgendaStatus, StatusColorConfig>> = Object.freeze({
    [AgendaStatus.PENDING]: Object.freeze({
      bg: 'hsl(43, 93%, 92%)',
      border: 'hsl(43, 92%, 66%)',
      text: 'hsl(43, 44%, 30%)'
    }),
    [AgendaStatus.CONFIRMED]: Object.freeze({
      bg: 'hsl(94, 93%, 92%)',
      border: 'hsl(94, 92%, 66%)',
      text: 'hsl(94, 44%, 30%)'
    }),
    [AgendaStatus.CANCELED]: Object.freeze({
      bg: 'hsl(4, 93%, 92%)',
      border: 'hsl(4, 92%, 66%)',
      text: 'hsl(4, 44%, 30%)'
    }),
    [AgendaStatus.COMPLETED]: Object.freeze({
      bg: 'hsl(243, 93%, 92%)',
      border: 'hsl(243, 92%, 66%)',
      text: 'hsl(243, 44%, 30%)'
    })
  });

  getStatusColors(status: AgendaStatus): StatusColorConfig {
    const cfg = this.statusColors[status] ?? this.statusColors[AgendaStatus.PENDING];
    return Object.freeze({ bg: cfg.bg, border: cfg.border, text: cfg.text });
  }

  getAllStatusColors(): Record<AgendaStatus, StatusColorConfig> {
    const result: Partial<Record<AgendaStatus, StatusColorConfig>> = {};
    (Object.keys(this.statusColors) as unknown as Array<keyof typeof this.statusColors>).forEach((key) => {
      const k = Number(key) as AgendaStatus;
      const v = this.statusColors[k];
      result[k] = Object.freeze({ bg: v.bg, border: v.border, text: v.text });
    });
    return result as Record<AgendaStatus, StatusColorConfig>;
  }

  getStatusColorsWithAlpha(status: AgendaStatus, alpha: number): StatusColorConfig {
    const clampedAlpha = this.clampAlpha(alpha);
    const base = this.getStatusColors(status);

    return Object.freeze({
      bg: this.hslToHslaIfPossible(base.bg, clampedAlpha),
      border: this.hslToHslaIfPossible(base.border, clampedAlpha),
      text: base.text
    });
  }

  private clampAlpha(alpha: number): number {
    if (typeof alpha !== 'number' || Number.isNaN(alpha)) return 1;
    return Math.max(0, Math.min(1, alpha));
  }

  private hslToHslaIfPossible(color: string, alpha: number): string {
    if (!color || typeof color !== 'string') return color;

    const hslRegex = /^\s*hsl\(\s*([0-9]+(?:\.[0-9]+)?)\s*,\s*([0-9]+(?:\.[0-9]+)?)%\s*,\s*([0-9]+(?:\.[0-9]+)?)%\s*\)\s*$/i;
    const match = color.match(hslRegex);
    if (!match) return color;

    const [, h, s, l] = match;
    return `hsla(${h}, ${s}%, ${l}%, ${alpha})`;
  }
}
